import Button from "@/components/Button";
import { noteSubmitted } from "@/gtmEvents/notes";
import useCurrentUser from "@/hooks/useCurrentUser";
import pushToDataLayer from "@/lib/pushToDataLayer";
import Form from "@/src/notes/components/Form";
import List from "@/src/notes/components/List";
import useNotes from "@/src/notes/hooks/useNotes";
import type { NoteType } from "@/src/notes/types/NoteType";
import clsx from "clsx";
import { observer } from "mobx-react";
import Link from "next/link";

const Notes = ({
  elementId,
  chefId,
  initialNotes,
  isPremiumElement = false,
}: {
  elementId: number;
  chefId?: number;
  initialNotes?: NoteType[];
  isPremiumElement: boolean;
}) => {
  const { currentUser, isLoggedIn, isFetching } = useCurrentUser();

  const {
    notes,
    totalNotesIncludingReplies,
    totalPages,
    isLoadingMore,
    isLoading,
    size,
    error,
    loadMore,
  } = useNotes({
    elementId,
    initialNotes,
  });

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const handleCreateComplete = (note: NoteType) => {
    pushToDataLayer(noteSubmitted, {
      recipeId: note.element?.id,
      recipeTitle: note.element?.title,
      recipeSlug: note.element?.slug,
      noteUid: note.uid,
    });
  };

  const displayNotes = isLoading ? initialNotes || [] : notes;
  const displayTotalNotes = isLoading
    ? initialNotes?.length || 0
    : totalNotesIncludingReplies;

  const messageLinkClasses = clsx(
    "underline decoration-zinc-300 underline-offset-2 transition ease-out hover:text-zinc-950 hover:decoration-zinc-400"
  );

  const userIsPremium = currentUser?.plan === 2;

  const canComment = (userIsPremium && isPremiumElement) || !isPremiumElement;

  return (
    <div className={clsx("Notes", "space-y-8")}>
      <div>
        <h3 className={clsx("mb-4 font-spatial text-lg lg:text-2xl")}>
          {displayTotalNotes === 0
            ? "Be the first to leave a note"
            : "Leave a note"}
        </h3>

        <Form
          elementId={elementId}
          onCreateComplete={handleCreateComplete}
          isDisabled={!isLoggedIn || !canComment}
        />

        {!isFetching && !isLoggedIn && (
          <div
            className={clsx("mt-4 text-center font-body text-sm text-zinc-500")}
          >
            <Link href="/signin" className={clsx(messageLinkClasses)}>
              You need to be logged in
            </Link>{" "}
            to add a note
          </div>
        )}

        {!isFetching && isLoggedIn && !canComment && (
          <div
            className={clsx("mt-4 text-center font-body text-sm text-zinc-500")}
          >
            <Link href="/premium" className={clsx(messageLinkClasses)}>
              Upgrade to a Mob Premium membership
            </Link>{" "}
            to add a note
          </div>
        )}
      </div>
      {displayTotalNotes > 0 && (
        <div>
          <h3
            className={clsx("mb-4 font-spatial text-2xl", {
              "flex items-center gap-2": displayNotes.length > 0,
            })}
          >
            <div>Notes</div>{" "}
            {displayTotalNotes > 0 && <div>({displayTotalNotes})</div>}
          </h3>

          <List notes={displayNotes} chefId={chefId} canReply={canComment} />

          {!isLoading && size < totalPages && (
            <div className={clsx("flex justify-center")}>
              <Button
                onClick={loadMore}
                isDisabled={isLoadingMore}
                isLoading={isLoadingMore}
              >
                Load more notes
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default observer(Notes);
